import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi, AuthAPIError } from '@/lib/auth-api';
import { Loader2, AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';

interface ResetPasswordFormProps {
	resetKey: string;
	resetLogin: string;
	onSuccess: () => void;
	onBack: () => void;
}

export function ResetPasswordForm({
	resetKey,
	resetLogin,
	onSuccess,
	onBack,
}: ResetPasswordFormProps) {
	const { t } = useTranslation();
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isValidating, setIsValidating] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [keyError, setKeyError] = useState<string | null>(null);

	// Validate key on mount
	useEffect(() => {
		const validateKey = async () => {
			if (!resetKey || !resetLogin) {
				setKeyError(t('Chýbajú parametre pre obnovenie hesla.'));
				setIsValidating(false);
				return;
			}

			try {
				const response = await authApi.validateResetKey({
					key: resetKey,
					login: resetLogin,
				});

				if (response.valid) {
					// Key is valid, proceed to show the form.
				} else {
					setKeyError(t('Neplatný odkaz na obnovenie hesla.'));
				}
			} catch (err) {
				if (err instanceof AuthAPIError) {
					setKeyError(err.message);
				} else {
					setKeyError(t('Neplatný alebo expirovaný odkaz.'));
				}
			} finally {
				setIsValidating(false);
			}
		};

		validateKey();
	}, [resetKey, resetLogin, t]);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			setError(null);

			// Validate passwords match
			if (password !== confirmPassword) {
				setError(t('Heslá sa nezhodujú.'));
				return;
			}

			// Validate password strength
			if (password.length < 8) {
				setError(t('Heslo musí mať aspoň 8 znakov.'));
				return;
			}

			setIsLoading(true);

			try {
				const response = await authApi.setPassword({
					key: resetKey,
					login: resetLogin,
					password,
				});

				if (response.success) {
					onSuccess();
				} else {
					setError(response.message || t('Nepodarilo sa zmeniť heslo.'));
				}
			} catch (err) {
				if (err instanceof AuthAPIError) {
					setError(err.message);
				} else {
					setError(t('Nastala neočakávaná chyba.'));
				}
			} finally {
				setIsLoading(false);
			}
		},
		[password, confirmPassword, resetKey, resetLogin, onSuccess, t]
	);

	// Loading state while validating key
	if (isValidating) {
		return (
			<div
				data-ui="reset-password-loading"
				className="flex flex-col items-center justify-center py-8 space-y-4"
			>
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
				<p className="text-sm text-muted-foreground">{t('Overujem odkaz...')}</p>
			</div>
		);
	}

	// Key validation error
	if (keyError) {
		return (
			<div data-ui="reset-password-key-error" className="space-y-4">
				<div
					data-ui="reset-password-key-error-alert"
					role="alert"
					className="flex items-center gap-2 rounded-lg border border-destructive/50 px-4 py-3 text-sm text-destructive"
				>
					<AlertCircle className="h-4 w-4 shrink-0" />
					<span>{keyError}</span>
				</div>

				<p className="text-sm text-muted-foreground text-center">
					{t('Požiadajte o nový odkaz na obnovenie hesla.')}
				</p>

				<Button
					data-ui="reset-password-back-btn"
					type="button"
					variant="outline"
					size="lg"
					className="w-full"
					onClick={onBack}
				>
					<ArrowLeft className="mr-2 h-4 w-4" />
					{t('Späť na prihlásenie')}
				</Button>
			</div>
		);
	}

	return (
		<form data-ui="reset-password-form" onSubmit={handleSubmit} className="space-y-4">
			{/* Visible email field for password managers (Chrome, Firefox, etc.) */}
			<div className="space-y-2">
				<Label htmlFor="email">{t('Email')}</Label>
				<Input
					id="email"
					data-ui="reset-password-email-input"
					name="username"
					type="email"
					autoComplete="username"
					value={resetLogin}
					readOnly
					tabIndex={-1}
					className="bg-muted text-muted-foreground cursor-default"
				/>
			</div>

			{error && (
				<div
					data-ui="reset-password-error-alert"
					role="alert"
					className="flex items-center gap-2 rounded-lg border border-destructive/50 px-4 py-3 text-sm text-destructive"
				>
					<AlertCircle className="h-4 w-4 shrink-0" />
					<span>{error}</span>
				</div>
			)}

			<div className="space-y-2">
				<Label htmlFor="password">{t('Nové heslo')}</Label>
				<div className="relative">
					<Input
						id="password"
						data-ui="reset-password-password-input"
						name="password"
						type={showPassword ? 'text' : 'password'}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						autoComplete="new-password"
						autoFocus
						disabled={isLoading}
						required
						minLength={8}
					/>
					<Button
						type="button"
						data-ui="reset-password-toggle-password-btn"
						variant="ghost"
						size="sm"
						className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
						onClick={() => setShowPassword(!showPassword)}
					>
						{showPassword ? (
							<EyeOff className="h-4 w-4 text-gray-400" />
						) : (
							<Eye className="h-4 w-4 text-gray-400" />
						)}
					</Button>
				</div>
				<p className="text-xs text-muted-foreground">{t('Minimálne 8 znakov')}</p>
			</div>

			<div className="space-y-2">
				<Label htmlFor="confirmPassword">{t('Potvrďte heslo')}</Label>
				<Input
					id="confirmPassword"
					data-ui="reset-password-confirm-input"
					name="confirmPassword"
					type={showPassword ? 'text' : 'password'}
					value={confirmPassword}
					onChange={(e) => setConfirmPassword(e.target.value)}
					autoComplete="new-password"
					disabled={isLoading}
					required
				/>
			</div>

			<div className="space-y-2 pt-2">
				<Button
					type="submit"
					data-ui="reset-password-submit-btn"
					size="lg"
					className="w-full"
					disabled={isLoading || !password || !confirmPassword}
				>
					{isLoading ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							{t('Ukladám...')}
						</>
					) : (
						t('Nastaviť nové heslo')
					)}
				</Button>

				<Button
					type="button"
					data-ui="reset-password-back-to-login-btn"
					variant="ghost"
					size="lg"
					className="w-full"
					onClick={onBack}
					disabled={isLoading}
				>
					<ArrowLeft className="mr-2 h-4 w-4" />
					{t('Späť na prihlásenie')}
				</Button>
			</div>
		</form>
	);
}
